import { TextInput } from "@/shared/coralreef";
import { useOnClickOutside } from "@/shared/coralreef/hooks/useOnClickOutside";
import { px, rem } from "csx";
import { Horizontal, Stretch } from "gls/lib";
import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Site } from "@/shared/context/sites.graphql";
import { style } from "typestyle";
import { MappedSite } from "../toolbar";
import { SiteCagePickerButton } from "./SiteCagePickerButton";
import { SitesList } from "./SitesList";
import { useSitePickerData } from "./hooks/useSitesPickerData";

interface SiteCagePickerProps {}

export const SiteCagePicker: FC<SiteCagePickerProps> = React.memo(() => {
  const { t } = useTranslation();

  const { loading, sites, setShowList, setSiteSearch, siteSearch, showList } =
    useSitePickerData();

  const ref = useOnClickOutside(
    useCallback(() => {
      setSiteSearch(undefined);
      setShowList(false);
    }, [setShowList, setSiteSearch]),
  );

  return (
    <Stretch
      ref={ref}
      className={style({ position: "relative" })}
      height={px(36)}
      data-testid="site-cage-picker"
    >
      {showList ? (
        <Horizontal width={rem(12)}>
          <TextInput
            placeholder={t("Search site")}
            value={siteSearch}
            onChange={setSiteSearch}
          />
        </Horizontal>
      ) : (
        <SiteCagePickerButton
          onClick={(e) => {
            e.stopPropagation();
            setShowList(!showList);
          }}
        />
      )}

      <SitesList
        open={showList}
        isLoading={loading}
        sites={sites}
        search={siteSearch}
      />
    </Stretch>
  );
});

export const mapSiteCagesByGroup = (
  sites: Site[] | undefined,
): MappedSite[] => {
  return (sites || []).map((site) => {
    const groupIds = site.cageGroups.map((group) => group.id);

    const grouplessCages = site.cages.filter(
      (cage) => cage.groupId === undefined || !groupIds.includes(cage.groupId),
    );

    const groupsWithCages = site.cageGroups.map((group) => ({
      id: group.id,
      name: group.name,
      cages: site.cages.filter((cage) => cage.groupId === group.id),
    }));

    return {
      id: site.id,
      name: site.name,
      machineId: site.machineId,
      cages: grouplessCages,
      cageGroups: groupsWithCages.filter((group) => group.cages.length !== 0),
      timezone: site.timezone,
      createdAt: site.createdAt,
      machines: site.machines,
    };
  });
};
